import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ButtonDialog from "default/components/ButtonDialog";
import Error from "default/components/Error";
import InputEmail from "default/components/InputEmail";
import Loading from "default/components/Loading";
import PayPayButton from "default/components/PayPayButton";
import StyledButton from "default/components/StyledButton";
import { useUrlParams } from "hooks/useUrlParams";
import useSystemTicketState from "hooks/useSystemTicketState";
import { stripePromise } from "utils/stripe";
import {
  getPayPayPaymentStatus,
  getSystemUnconfirmedPayment,
  getSystemUserEmail,
  listSystemTickets,
  purchaseSystemTicketPaypay,
  purchaseSystemTicketStripe,
  updateSystemUserEmail,
} from "utils/api";

import OmagunLayout from "../components/OmagunLayout";
import OmagunSectionHeading from "../components/OmagunSectionHeading";

const PURCHASE_METHOD = { STRIPE: "stripe", PAYPAY: "paypay" };

const TICKET_ORDER = {
  "10days_first": 0,
  "3days_first": 1,
  "7days_extension": 0,
  "10days_continuation": 0,
};

const SERIF_JP = '"Noto Serif JP", serif';

// Figma の Ticket セクションの文字組み。
// 主プラン(month_plan)は 20px/40px、副プラン(week_plan)は 20px/30px と行送りだけが異なる。
const PLAN_LINE_HEIGHT = { primary: 40, secondary: 30 };

// Figma: 「グンマ県民挑戦権 10日間」は空白位置で改行し、2行に組まれる。
const splitPlanName = (name) => String(name || "").split(/[\s　]+/).filter(Boolean);

const TicketPlan = ({ ticket, onBuy, isPrimary = false, sx = {} }) => {
  const lineHeight = isPrimary ? PLAN_LINE_HEIGHT.primary : PLAN_LINE_HEIGHT.secondary;

  return (
    <Box sx={{ width: "100%", textAlign: "center", ...sx }}>
      {splitPlanName(ticket.name).map((line) => (
        <Typography
          key={line}
          sx={{ fontFamily: SERIF_JP, fontWeight: 400, fontSize: "20px", lineHeight: `${lineHeight}px`, letterSpacing: ".1em" }}
        >
          {line}
        </Typography>
      ))}
      {ticket.description && (
        <Typography sx={{ fontFamily: SERIF_JP, fontWeight: 400, fontSize: "13px", lineHeight: "30px", letterSpacing: ".1em" }}>
          {ticket.description}
        </Typography>
      )}
      <Typography sx={{ fontFamily: SERIF_JP, fontWeight: 700, fontSize: "20px", lineHeight: "40px", letterSpacing: ".1em" }}>
        {ticket.display_price || `¥${ticket.price}`}
      </Typography>
      <StyledButton
        aria-label={`${ticket.name}を購入`}
        onClick={() => onBuy(ticket.ticket_key)}
        sx={{
          // Figma: purchase_waku 126x39 / border 1.5px / radius 20px / shadow 0 4px 4px rgba(0,0,0,.25)
          // inline-flex のままだと行ボックスの strut 分だけ余白がずれるためブロック化する。
          display: "flex",
          mx: "auto",
          width: "126px",
          height: "39px",
          minHeight: "39px",
          mt: "10px",
          p: 0,
          border: "1.5px solid #FFFFFF",
          borderRadius: "20px",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          fontFamily: SERIF_JP,
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: "40px",
          letterSpacing: ".1em",
        }}
      >
        BUY
      </StyledButton>
      {ticket.notice && (
        <Typography sx={{ fontFamily: SERIF_JP, fontWeight: 700, fontSize: "11px", lineHeight: "40px", letterSpacing: ".1em" }}>
          {ticket.notice}
        </Typography>
      )}
    </Box>
  );
};

export default function Tickets() {
  const navigate = useNavigate();
  const { soulId, lng, work } = useUrlParams();
  const { t } = useTranslation([work]);
  const ticketState = useSystemTicketState(soulId);
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedTicketKey, setSelectedTicketKey] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [emailRequired, setEmailRequired] = useState(false);
  const [recoveryPayment, setRecoveryPayment] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    let active = true;
    if (!soulId) {
      setIsLoading(false);
      return () => { active = false; };
    }
    const load = async () => {
      try {
        const [ticketResult, paymentResult] = await Promise.all([
          listSystemTickets(soulId),
          getSystemUnconfirmedPayment(soulId).catch(() => ({ unconfirmed_payment: null })),
        ]);
        if (!active) return;
        setTickets(ticketResult.data || []);
        const payment = paymentResult.unconfirmed_payment;
        if (payment) {
          const status = await getPayPayPaymentStatus(payment.merchant_payment_id, {
            soulId,
            purchaseType: payment.purchase_type,
            ticketKey: payment.ticket_key,
            useSystemApi: true,
          });
          if (active && status?.status === "COMPLETED") setRecoveryPayment(payment);
        }
      } catch (error) {
        if (active) setIsError(true);
      } finally {
        if (active) setIsLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [soulId]);

  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket.ticket_key === selectedTicketKey),
    [tickets, selectedTicketKey]
  );

  const purchase = useCallback(async (method, ticket = selectedTicket) => {
    if (!ticket) return;
    setIsProcessing(true);
    try {
      if (method === PURCHASE_METHOD.STRIPE) {
        const result = await purchaseSystemTicketStripe(soulId, ticket.ticket_key, ticket.price);
        const stripe = await stripePromise;
        if (stripe) await stripe.redirectToCheckout({ sessionId: result.session_id });
      } else {
        const result = await purchaseSystemTicketPaypay(soulId, ticket.ticket_key, ticket.price);
        window.location.href = result.url;
      }
    } catch (error) {
      setErrorMessage(
        error.status === 409
          ? "この購入は既に処理されています"
          : error.status === 422
          ? "価格情報が変更されました。ページを更新してください"
          : "購入処理に失敗しました"
      );
    } finally {
      setIsProcessing(false);
    }
  }, [soulId, selectedTicket]);

  const selectMethod = useCallback(async (method) => {
    setSelectedMethod(method);
    try {
      const email = await getSystemUserEmail();
      if (!email.email_address) {
        setEmailRequired(true);
        setSelectedTicketKey((key) => key);
        return;
      }
      setSelectedTicketKey((key) => key);
      await purchase(method);
    } catch (error) {
      setErrorMessage("メールアドレスを確認できませんでした");
    }
  }, [purchase]);

  const retryAfterEmail = useCallback(
    () => purchase(selectedMethod),
    [purchase, selectedMethod]
  );

  // Hero を描かない状態は、Hero 直下から始まる Tickets 背景だと上に空白が空くので、
  // MyPage や Quests と同じ通常の暗色背景に揃える。
  if (isLoading) {
    return <OmagunLayout page="Tickets" backgroundVariant="dark"><Box sx={{ minHeight: "80vh", display: "grid", placeItems: "center" }}><Loading /></Box></OmagunLayout>;
  }
  if (isError) {
    return <OmagunLayout page="Tickets" backgroundVariant="dark"><Box sx={{ minHeight: "80vh", display: "grid", placeItems: "center" }}><Error /></Box></OmagunLayout>;
  }
  if (emailRequired) {
    return (
      <OmagunLayout page="Tickets" backgroundVariant="dark">
        <InputEmail onClickPurchase={retryAfterEmail} updateEmailFn={updateSystemUserEmail} />
      </OmagunLayout>
    );
  }

  const expired = ticketState.hasPurchased && ticketState.ticketState?.is_expired;
  const rawStoryLines = t(`${work}:${expired ? "epilogue" : "story"}.todoroki`, { returnObjects: true });
  // Figma の Story は行送り46px(=23px×2)で均一に組まれており、段落の空行は入らない。
  const storyLines = Array.isArray(rawStoryLines) ? rawStoryLines.filter((line) => line.trim()) : [];
  const orderedTickets = [...tickets].sort(
    (left, right) => (TICKET_ORDER[left.ticket_key] ?? 99) - (TICKET_ORDER[right.ticket_key] ?? 99)
  );

  return (
    <OmagunLayout page="Tickets">
      <Box sx={{ position: "relative", zIndex: 1, width: "100%", aspectRatio: "414 / 586", overflow: "visible" }}>
        <Box
          component="img"
          src="/omagun/tickets/hero.webp"
          alt="お前はまだグンマを知らない 令和版 SoulChat"
          sx={{
            position: "absolute",
            inset: "0 auto auto 0",
            width: "100%",
            height: "auto",
            display: "block",
            // Hero本体(586px)より下の63pxだけを背景へ溶かす。
            // Hero全高649pxに対し、586 / 649 = 90.29275809%。
            WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 90.29275809%, transparent 100%)",
            maskImage: "linear-gradient(to bottom, #000 0%, #000 90.29275809%, transparent 100%)",
          }}
        />
      </Box>

      <Box sx={{ position: "relative", zIndex: 2, px: 4, pt: "49px", pb: 6, textAlign: "center" }}>
        {/* Epilogue はアクセントが 5文字目("Epil-o-gue")。 */}
        {expired
          ? <OmagunSectionHeading label="Epilogue" accentIndex={4} lineHeight={40} />
          : <OmagunSectionHeading label="Story" lineHeight={40} />}
        <Typography sx={{ mt: "11px", fontFamily: '"TamanegiKaishoGeki", serif', fontSize: "20px", lineHeight: "30px", letterSpacing: ".1em" }}>
          - 轟二矢 -
        </Typography>
        <Typography
          sx={{
            width: 316,
            maxWidth: "100%",
            mx: "auto",
            mt: "11px",
            fontFamily: SERIF_JP,
            fontWeight: 700,
            fontSize: "16px",
            lineHeight: "46px",
            color: "#fff",
          }}
        >
          {storyLines.map((line, index) => <React.Fragment key={index}>{index > 0 && <br />}{line}</React.Fragment>)}
        </Typography>

        <OmagunSectionHeading label="Ticket" lineHeight={52} sx={{ mt: "34px" }} />
        <Box sx={{ mt: "8px" }}>
          {orderedTickets.length ? orderedTickets.map((ticket, index) => (
            <TicketPlan
              key={ticket.ticket_key}
              ticket={ticket}
              onBuy={setSelectedTicketKey}
              isPrimary={index === 0}
              sx={{ mb: index === orderedTickets.length - 1 ? 0 : "54px" }}
            />
          )) : (
            <Typography sx={{ py: 4 }}>現在購入できるチケットはありません</Typography>
          )}
        </Box>
      </Box>

      <ButtonDialog
        open={Boolean(selectedTicketKey) && !emailRequired}
        title="購入方法を選択してください"
        handleClose={() => setSelectedTicketKey(null)}
        rawButtons={[
          <StyledButton key="stripe" disabled={isProcessing} onClick={() => selectMethod(PURCHASE_METHOD.STRIPE)} sx={{ width: "100%" }}>クレジットカード</StyledButton>,
          ...(lng === "ja" ? [<PayPayButton key="paypay" disabled={isProcessing} onClick={() => selectMethod(PURCHASE_METHOD.PAYPAY)} />] : []),
        ]}
      />
      <ButtonDialog
        open={Boolean(recoveryPayment)}
        title="PayPay購入による処理が正常に完了していない可能性があります。"
        handleClose={() => setRecoveryPayment(null)}
        buttons={[{
          label: "購入状況を確認",
          onClick: () => navigate(`/${work}/paypay/check?soul_id=${soulId}&merchant_payment_id=${recoveryPayment?.merchant_payment_id}&purchase_type=${recoveryPayment?.purchase_type || "ticket"}&ticket_key=${recoveryPayment?.ticket_key || ""}&lng=${lng}`),
        }]}
      />
      <ButtonDialog open={Boolean(errorMessage)} title={errorMessage || ""} handleClose={() => setErrorMessage(null)} buttons={[{ label: "OK", onClick: () => setErrorMessage(null) }]} />
    </OmagunLayout>
  );
}
